import type { ServicePageData } from './types';

const pullUpBanners: ServicePageData = {
  slug: 'pull-up-banners',
  eyebrow: 'Signage',
  title: 'Set up in seconds with',
  titleAccent: 'pull-up banners.',
  intro:
    'A roller banner is the quickest way to add a professional backdrop to any stand. Pull the cassette up, click the pole into place, and you have a clean, branded presence ready in under a minute.',
  heroImage: '/home/signboards/signboards-banners.jpg',

  useCases: [
    { title: 'Exhibitions and trade shows', text: 'A tidy, branded backdrop that goes up and down in minutes.' },
    { title: 'Receptions and offices', text: 'A permanent talking point by the front desk or in a meeting room.' },
    { title: 'Events and conferences', text: 'Pack it in the car boot and set up wherever the stand is.' },
    { title: 'Pop-up retail', text: 'Reinforce your branding around a market stall or promotional table.' },
    { title: 'Product launches', text: 'Pair two or three banners either side of a table for extra impact.' },
  ],

  optionsHeading: 'Choose your banner',
  optionsIntro: 'Every roller banner is printed in-house and comes with its own carry bag.',
  options: [
    {
      id: 'standard-roller',
      title: 'Standard roller',
      lead: 'A dependable, budget-friendly banner stand for regular use.',
      body: 'Our most popular option for a single event or a stand that travels often. The pole assembles in seconds and the graphic tensions flat with no creases.',
      image: '/home/signboards/signboards-banners.jpg',
      specs: [
        { label: 'Size', value: '850mm or 1000mm wide, roughly 2000mm tall when extended.' },
        { label: 'Base', value: 'Compact aluminium cassette base.' },
        { label: 'Print', value: 'Full-colour, single-sided.' },
        { label: 'Includes', value: 'Telescopic pole, carry bag.' },
      ],
    },
    {
      id: 'premium-roller',
      title: 'Premium roller',
      lead: 'A wider, sturdier base for stands that get used week after week.',
      body: 'Built around a wider footprint that stands firmer in a busy venue, with a swappable cassette so you can change the graphic without buying a whole new stand.',
      image: '/home/pavement-signs/pavement-sign-mockup.jpg',
      specs: [
        { label: 'Size', value: '850mm or 1000mm wide, roughly 2000mm tall when extended.' },
        { label: 'Base', value: 'Wider, weighted aluminium base for extra stability.' },
        { label: 'Cassette', value: 'Swappable, so the graphic can be reprinted and refitted separately from the stand.' },
        { label: 'Includes', value: 'Telescopic pole, carry bag.' },
      ],
    },
    {
      id: 'double-sided',
      title: 'Double-sided',
      lead: 'Readable from both directions, ideal for an open floor or a walkway.',
      body: 'Two graphics run back to back through the same cassette, so the banner reads correctly whichever side people approach from. Useful where the stand is not against a wall.',
      image: '/home/signboards/signboards-outpost.jpg',
      specs: [
        { label: 'Size', value: '850mm or 1000mm wide, roughly 2000mm tall when extended.' },
        { label: 'Print', value: 'Two full-colour graphics, back to back.' },
        { label: 'Base', value: 'Standard or premium base, your choice.' },
        { label: 'Includes', value: 'Telescopic pole, carry bag.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Your artwork is printed directly onto the roller banner fabric on our in-house wide-format printer, handling photography, gradients and fine detail without a set-up fee.',
    },
    {
      title: 'Cassette reprints',
      text: 'On a premium roller, the graphic cassette can be replaced on its own, so a message or offer update does not mean buying a new base and pole.',
    },
  ],

  gallery: [
    '/home/signboards/signboards-banners.jpg',
    '/home/signboards/signboards-outpost.jpg',
    '/home/signboards/signboards-mayfield.jpg',
    '/home/signboards/signboards-blackheart.jpg',
    '/home/pavement-signs/pavement-sign-mockup.jpg',
    '/home/signboards/signboards-walkers.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Every roller banner is delivered ready to use, in its own carry bag, with clear instructions for setting up and packing away.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Care',
      text: 'Roll the banner back into its cassette when not in use to keep the graphic protected and creases out. Wipe clean with a soft, damp cloth.',
    },
    {
      title: 'Turnaround',
      text: '5 to 10 working days from approval. Need it sooner for a booked event? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'Can I reuse the stand and just replace the graphic?',
      a: 'Yes, on the premium roller. The cassette is swappable, so you only need to reorder the printed graphic rather than the whole stand.',
    },
    {
      q: 'How long does it take to set up?',
      a: 'Under a minute. Extend the pole, click it into the base and lift the graphic out of the cassette.',
    },
    {
      q: 'Can I get a matching pair for either side of a table?',
      a: 'Yes, order two or three banners with the same artwork for a stronger stand presence.',
    },
  ],

  cta: {
    title: 'Get your free roller banner quote and mockup.',
    text: 'Send us your logo or artwork and we will put together a free visual before you commit.',
    image: '/home/signboards/signboards-banners.jpg',
  },
};

export default pullUpBanners;
